import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Interest { label: string; keywords: string[] }

// Common interest areas offered as one-tap additions.
const CURATED_INTERESTS = [
  'Technologie', 'Intelligence artificielle', 'Science', 'Économie', 'Politique',
  'Politique québécoise', 'Climat & environnement', 'Santé', 'Sport', 'Culture',
  'Cinéma', 'Musique', 'Jeux vidéo', 'Affaires & startups', 'Espace',
];
interface Suggestion { label: string; count: number; kind: 'theme' | 'person' | 'place' }
interface NewsItem { id: string; source: string; headline: string; fetchedAt: number; language: string }

// Curated, well-known RSS sources — one-click add/remove (the manual field below
// still accepts any custom URL).
const CURATED_FEEDS: { name: string; url: string; lang: string }[] = [
  { name: 'CNN', url: 'http://rss.cnn.com/rss/cnn_topstories.rss', lang: 'en' },
  { name: 'Fox News', url: 'https://moxie.foxnews.com/google-publisher/latest.xml', lang: 'en' },
  { name: 'Euronews', url: 'https://www.euronews.com/rss', lang: 'en' },
  { name: 'BBC World', url: 'https://feeds.bbci.co.uk/news/world/rss.xml', lang: 'en' },
  { name: 'The Guardian', url: 'https://www.theguardian.com/world/rss', lang: 'en' },
  { name: 'NPR', url: 'https://feeds.npr.org/1001/rss.xml', lang: 'en' },
  { name: 'Al Jazeera', url: 'https://www.aljazeera.com/xml/rss/all.xml', lang: 'en' },
  { name: 'NYT World', url: 'https://rss.nytimes.com/services/xml/rss/nyt/World.xml', lang: 'en' },
  { name: 'Le Monde', url: 'https://www.lemonde.fr/rss/une.xml', lang: 'fr' },
  { name: 'France 24', url: 'https://www.france24.com/fr/rss', lang: 'fr' },
  { name: 'Le Devoir', url: 'https://www.ledevoir.com/rss/manchettes.xml', lang: 'fr' },
  { name: 'Radio-Canada', url: 'https://ici.radio-canada.ca/rss/4159', lang: 'fr' },
  { name: 'La Presse', url: 'https://www.lapresse.ca/actualites/rss', lang: 'fr' },
  { name: 'El País', url: 'https://feeds.elpais.com/mrss-s/pages/ep/site/elpais.com/portada', lang: 'es' },
  { name: 'TechCrunch', url: 'https://techcrunch.com/feed/', lang: 'en' },
  { name: 'Ars Technica', url: 'https://feeds.arstechnica.com/arstechnica/index', lang: 'en' },
];

export function NewsPane() {
  const qc = useQueryClient();

  const interestsQ = useQuery<{ interests: Interest[] }>({ queryKey: ['news', 'interests'], queryFn: () => api('/api/news/interests') });
  const feedsQ = useQuery<{ feeds: string[] }>({ queryKey: ['news', 'feeds'], queryFn: () => api('/api/news/feeds') });
  const itemsQ = useQuery<{ items: NewsItem[] }>({ queryKey: ['news', 'items'], queryFn: () => api('/api/news/items') });

  const [interests, setInterestsState] = useState<string[]>([]);
  const [feeds, setFeedsState] = useState<string[]>([]);
  const [newInterest, setNewInterest] = useState('');
  const [newFeed, setNewFeed] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const [initI, setInitI] = useState(false);
  const [initF, setInitF] = useState(false);

  useEffect(() => { if (interestsQ.data && !initI) { setInterestsState(interestsQ.data.interests.map((i) => i.label)); setInitI(true); } }, [interestsQ.data, initI]);
  useEffect(() => { if (feedsQ.data && !initF) { setFeedsState(feedsQ.data.feeds); setInitF(true); } }, [feedsQ.data, initF]);

  async function saveInterests(next: string[]) {
    setInterestsState(next);
    await api('/api/news/interests', { method: 'PUT', body: JSON.stringify({ interests: next }) });
    qc.invalidateQueries({ queryKey: ['news', 'interests'] });
  }
  async function saveFeeds(next: string[]) {
    setFeedsState(next);
    await api('/api/news/feeds', { method: 'PUT', body: JSON.stringify({ feeds: next }) });
    qc.invalidateQueries({ queryKey: ['news', 'feeds'] });
  }
  async function runWizard() {
    setMsg('Reading your vault…');
    const r = await api<{ suggestions: Suggestion[] }>('/api/news/interests/suggest');
    setSuggestions(r.suggestions);
    setPicked(new Set(r.suggestions.slice(0, 6).map((s) => s.label)));
    setMsg(r.suggestions.length ? null : 'Your vault is still thin — add memories first, or type interests manually.');
  }
  function addPicked() {
    const merged = [...new Set([...interests, ...[...picked]])];
    void saveInterests(merged);
    setSuggestions(null); setPicked(new Set());
  }
  async function refresh() {
    setMsg('Fetching feeds…');
    const r = await api<{ result: { inserted: number; skipped: number; errors: { feed: string; error: string }[] } }>('/api/news/refresh', { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['news', 'items'] });
    setMsg(`Inserted ${r.result.inserted}, skipped ${r.result.skipped}${r.result.errors.length ? `, ${r.result.errors.length} feed error(s)` : ''}.`);
  }
  async function webSearch() {
    setMsg('Searching the web for your interests…');
    const r = await api<{ result: { inserted: number; found: number; reason?: string } }>('/api/news/web-search', { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['news', 'items'] });
    setMsg(r.result.reason && r.result.inserted === 0
      ? (r.result.reason === 'no_api' ? 'Web search needs API mode + an Anthropic key.' : r.result.reason === 'no_interests' ? 'Add interests first.' : `Nothing inserted (${r.result.reason}).`)
      : `Web search: inserted ${r.result.inserted} of ${r.result.found} found.`);
  }

  return (
    <>
      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">News &amp; areas of interest</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          Define what you care about. News flows from these interests (plus what your vault already discusses)
          and feeds the daily question + the alignment engine. In dev, Claude Desktop web-searches your
          interests and injects the news; the API will do it automatically later.
        </p>
      </div>

      {/* Wizard */}
      <div className="rounded-xl border border-amber/20 bg-amber/[0.04] p-4 mb-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11.5px] text-amber/80">Interest wizard</div>
          <button onClick={runWizard}
            className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">
            Suggest from my vault
          </button>
        </div>
        {suggestions && (
          <div className="mt-3 space-y-3">
            <p className="font-serif italic text-[13px] text-ink/60">Tap the ones that feel like you, then add them:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s) => {
                const on = picked.has(s.label);
                return (
                  <button key={`${s.kind}:${s.label}`} onClick={() => { const n = new Set(picked); on ? n.delete(s.label) : n.add(s.label); setPicked(n); }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-serif border transition-colors ${on ? 'bg-amber text-void border-amber' : 'border-ink/20 text-ink/60 hover:text-ink'}`}>
                    {s.label} <span className="opacity-50 text-[9px]">{s.kind}·{s.count}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={addPicked} disabled={picked.size === 0}
              className="px-4 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/70 hover:text-ink disabled:opacity-30">
              Add {picked.size} to interests
            </button>
          </div>
        )}
      </div>

      {/* Interests */}
      <div className="mb-5">
        <div className="text-[11px] text-ink/45 mb-2">Areas of interest</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {interests.length === 0 && <span className="font-serif italic text-ink/40 text-[13px]">None yet — run the wizard or add below.</span>}
          {interests.map((label) => (
            <span key={label} className="px-2.5 py-1 rounded-full border border-particle/30 text-particle text-[12px] font-serif flex items-center gap-1.5">
              {label}
              <button onClick={() => saveInterests(interests.filter((x) => x !== label))} className="text-ink/40 hover:text-red-300 text-[11px]">✕</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newInterest} onChange={(e) => setNewInterest(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newInterest.trim()) { void saveInterests([...new Set([...interests, newInterest.trim()])]); setNewInterest(''); } }}
            placeholder="add an interest (e.g. politique québécoise, jazz, jardinage)"
            className="flex-1 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
        </div>

        {/* Quick-add catalogue — common interest areas, one tap to add. */}
        <div className="mt-3">
          <div className="text-[10px] text-ink/30 mb-1.5">Quick add</div>
          <div className="flex flex-wrap gap-1.5">
            {CURATED_INTERESTS.filter((c) => !interests.some((i) => i.toLowerCase() === c.toLowerCase())).map((c) => (
              <button key={c} onClick={() => void saveInterests([...new Set([...interests, c])])}
                className="px-2.5 py-1 rounded-full border border-ink/15 text-ink/55 text-[12px] font-serif hover:text-ink hover:border-ink/35 transition-colors">
                + {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feeds */}
      <div className="mb-5 pt-4 border-t border-ink/[0.06]">
        <div className="text-[11px] text-ink/45 mb-2">Popular sources — tap to add/remove</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CURATED_FEEDS.map((s) => {
            const on = feeds.includes(s.url);
            return (
              <button key={s.url} title={s.url}
                onClick={() => saveFeeds(on ? feeds.filter((x) => x !== s.url) : [...new Set([...feeds, s.url])])}
                className={`px-2.5 py-1 rounded-full text-[11px] font-serif border transition-colors ${on ? 'bg-amber text-void border-amber' : 'border-ink/20 text-ink/60 hover:text-ink'}`}>
                {s.name} <span className="opacity-50 text-[8.5px] uppercase">{s.lang}</span>{on ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-ink/45 mb-2">RSS feeds (custom)</div>
        <ul className="space-y-1 mb-2">
          {feeds.map((f) => (
            <li key={f} className="flex items-center gap-2 font-mono text-[11px] text-ink/60">
              <button onClick={() => saveFeeds(feeds.filter((x) => x !== f))} className="text-ink/40 hover:text-red-300">✕</button>
              <span className="truncate">{f}</span>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input value={newFeed} onChange={(e) => setNewFeed(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && /^https?:\/\//.test(newFeed)) { void saveFeeds([...new Set([...feeds, newFeed.trim()])]); setNewFeed(''); } }}
            placeholder="https://www.ledevoir.com/rss/manchettes.xml"
            className="flex-1 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[12px] text-ink focus:border-ink/40 focus:outline-none" />
          <button onClick={refresh} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/70 hover:text-ink">Refresh now</button>
          <button onClick={webSearch} title="Use web search over your interests (API mode)"
            className="px-4 py-2 rounded-full border border-particle/30 text-[11.5px] text-particle/80 hover:text-particle">🔎 Web</button>
        </div>
      </div>

      {msg && <p className="font-serif italic text-[13px] text-ink/55 mb-3">{msg}</p>}

      {/* Recent items */}
      <div className="pt-4 border-t border-ink/[0.06]">
        <div className="text-[11px] text-ink/45 mb-2">Recent news ({itemsQ.data?.items.length ?? 0})</div>
        {itemsQ.data && itemsQ.data.items.length > 0 ? (
          <ul className="space-y-1.5 max-h-48 overflow-y-auto">
            {itemsQ.data.items.map((n) => (
              <li key={n.id} className="text-[13px]">
                <span className="text-[11px] text-ink/35 mr-2">{n.source}</span>
                <span className="font-serif text-ink/70">{n.headline}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-serif italic text-ink/40 text-[13px]">
            No news yet. In Claude Desktop: “regarde mes centres d’intérêt (get_interests), cherche l’actualité du jour, et injecte-la (inject_news)”.
          </p>
        )}
      </div>
    </>
  );
}
